import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { AppModule } from './app/app.module';
import { environment } from './environments/environment';
import {
  MSAL_INSTANCE,
  MSAL_GUARD_CONFIG,
  MSAL_INTERCEPTOR_CONFIG,
  MsalGuardConfiguration,
  MsalInterceptorConfiguration
} from '@azure/msal-angular';
import { BrowserCacheLocation, InteractionType, PublicClientApplication, LogLevel } from '@azure/msal-browser';
import { MsalConfig } from './app/classes/authentication/msal-config';
import { determineMSALConfigFile, determineMSALScope } from './app/common/scope-utility';
import { CVV_AUTH_CONFIGS, CvvAuthConfig } from './app/services/auth-config.service';

if (environment.production) {
  enableProdMode();
}

function loggerCallback(logLevel: any, message: string) {
  // Optionally log MSAL messages for debugging
  if (!environment.production && logLevel <= LogLevel.Warning) {
    console.log(`[MSAL] ${message}`);
  }
}

/**
 * Determines the gateway URL based on current environment.
 * Follows the crew-jumpseat-ui pattern.
 */
function getGatewayUrl(): string {
  const origin = window.location.origin;
  
  if (origin.includes('localhost')) {
    return '/crew-cvv-gateway'; // Use relative path for proxy to intercept
  }
  
  // For deployed environments, construct gateway URL
  return origin.replace('cvv', 'crew-cvv-gateway');
}

/**
 * Bootstrap the Angular application with MSAL configuration.
 * Loads MSAL config from static JSON files based on environment.
 */
const msalConfigUrl = determineMSALConfigFile();

fetch(msalConfigUrl)
  .then((response: Response) => {
    if (!response.ok) {
      throw new Error(`Failed to load MSAL config from ${msalConfigUrl}`);
    }
    return response.json();
  })
  .then(async (json: MsalConfig) => {
    // Use dynamic scope determination
    const dynamicScope = determineMSALScope();
    
    // Determine cache location
    const cacheLocation = json.cache?.cacheLocation === 'sessionStorage' 
      ? BrowserCacheLocation.SessionStorage 
      : BrowserCacheLocation.LocalStorage;

    // Build protected resource map with dynamic scope
    const protectedResourceMap = new Map<string, Array<string>>();
    protectedResourceMap.set('*', [dynamicScope]);

    // Create MSAL instance and initialize (required for MSAL v4)
    const msalInstance = new PublicClientApplication({
      auth: {
        clientId: json.auth.clientId,
        authority: json.auth.authority,
        redirectUri: json.auth.redirectUri || window.location.origin + '/',
        postLogoutRedirectUri: json.auth.postLogoutRedirectUri || window.location.origin + '/',
        navigateToLoginRequestUrl: json.auth.navigateToLoginRequestUrl ?? true
      },
      cache: {
        cacheLocation: cacheLocation,
        storeAuthStateInCookie: json.cache?.storeAuthStateInCookie ?? true
      },
      system: {
        loggerOptions: {
          loggerCallback,
          logLevel: environment.production ? LogLevel.Error : LogLevel.Info,
          piiLoggingEnabled: false
        }
      }
    });

    // MSAL v4 requires explicit initialization before use
    await msalInstance.initialize();

    // CVV Auth Config for services
    const cvvAuthConfig: CvvAuthConfig = {
      gatewayUrl: getGatewayUrl(),
      msalConfigUrl: msalConfigUrl
    };

    platformBrowserDynamic([
      {
        provide: MSAL_INSTANCE,
        useValue: msalInstance
      },
      {
        provide: MSAL_GUARD_CONFIG,
        useValue: {
          interactionType: InteractionType.Redirect,
          authRequest: {
            scopes: [dynamicScope]
          },
          loginFailedRoute: json.guard?.loginFailedRoute || '/unauthorized'
        } as MsalGuardConfiguration
      },
      {
        provide: MSAL_INTERCEPTOR_CONFIG,
        useValue: {
          interactionType: InteractionType.Redirect,
          protectedResourceMap: protectedResourceMap
        } as MsalInterceptorConfiguration
      },
      {
        provide: CVV_AUTH_CONFIGS,
        useValue: cvvAuthConfig
      }
    ])
      .bootstrapModule(AppModule)
      .catch((err) => console.error('Bootstrap error:', err));
  })
  .catch((err) => {
    console.error('Failed to initialize application:', err);
    // Show user-friendly error message
    document.body.innerHTML = `
      <div style="display: flex; justify-content: center; align-items: center; height: 100vh; font-family: Arial, sans-serif;">
        <div style="text-align: center;">
          <h1>Application Error</h1>
          <p>Failed to load application configuration. Please refresh the page or contact support.</p>
        </div>
      </div>
    `;
  });
